import moment from 'moment';
import { QueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { DropdownOption, NameObject, PrescriberNameObject, TableName, CookieColumnName } from './types';
import { PharmacyAddress } from './api';
import { MULTI_SELECT_DELINEATOR } from './constants';
import { getCookie, createCookie, deleteCookie } from 'design-system/src/cookieUtils';
import type { MultiselectAutocompleteOption } from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage.tsx';
import { UserDefaultFilters, User } from './auth';

export const requiredErrorString = 'Required';
export const requiredErrorMessage = <ErrorMessage>{requiredErrorString}</ErrorMessage>;

export const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    return '-';
  }

  // Use a regular expression to capture the different parts of the phone number
  const regex = /^(\d{3})(\d{3})(\d{4})$/;

  // Replace the matched pattern with the desired format
  const formattedNumber = phoneNumber.replace(regex, '($1) $2-$3');

  return formattedNumber;
};

export const phoneNumberUnformatted = (phoneNumber: string) => {
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Formats a datestring but does not convert it to the user's timezone. Good for birth dates.
 */
export const formatStandardDate = (dateString: string) => {
  return moment(dateString).format('MM/DD/YYYY');
};

/**
 * Formats a datestring into a simple date format (M/D/YY).
 * TODO: add better error handling -- if the date string isn't parsed correctly,
 * returns "Invalid date".
 */
export const formatSimpleDate = (dateString: string) => {
  return moment(dateString).format('M/D/YY');
};

/**
 * Formats a datestring and also converts it to the user's timezone. Do not use for birth dates.
 */
export const formatStandardDateLocaleString = (dateString: string) => {
  return moment(new Date(dateString).toLocaleDateString()).format('MM/DD/YYYY');
};

export function convertBooleanToYesNo(value: boolean | null) {
  return value === true ? 'Yes' : value === false ? 'No' : null;
}

export function convertYesNoToBoolean(value: 'Yes' | 'No' | null) {
  return value === 'Yes' ? true : value === 'No' ? false : null;
}

/**
 * Office names could be addresses, so we want to make sure anything that's an address is placed after actual office names
 * An address starting with a number is our approximation of identifying an address
 */
export const sortOfficeNames = (officeNameA: string, officeNameB: string): number => {
  const startsWithNumber = (str: string) => /\d/.test(str[0]);

  // If `a` starts with a number and `b` does not, `a` goes after `b`
  if (startsWithNumber(officeNameA) && !startsWithNumber(officeNameB)) return 1;

  // If `b` starts with a number and `a` does not, `b` goes after `a`
  if (!startsWithNumber(officeNameA) && startsWithNumber(officeNameB)) return -1;

  // Otherwise, sort alphabetically
  return officeNameA.localeCompare(officeNameB);
};

export const truncateLongText = (
  text: string,
  maxNumLength: number | undefined = 14,
  numCharsOnEnd: number | undefined = 4,
  includeTitle: boolean = true,
) => {
  if (text.length > maxNumLength) {
    const truncatedText = `${text.slice(0, maxNumLength - numCharsOnEnd)}...${numCharsOnEnd > 0 ? text.slice(-numCharsOnEnd) : ''}`;
    return includeTitle ? <span title={text}>{truncatedText}</span> : truncatedText;
  }
  return text;
};

export function stringsToDropdownOptions(options: string[], formatFn?: (option: string) => string) {
  return options.map((option) => ({ value: option, description: formatFn ? formatFn(option) : option }));
}

export const titleCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const formatDrugName = (name: string) => {
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const formattedName = (name: NameObject) => {
  return `${titleCase(name.first_name)} ${titleCase(name.last_name)}`;
};

export const namesToDropdownOptions = (names: NameObject[]) => {
  return names.map((item) => {
    return {
      value: `${item.first_name}+${item.last_name}`,
      description: formattedName(item),
    } as DropdownOption;
  });
};

export const prescriberNamesToDropdownOptions = (names: PrescriberNameObject[]) => {
  return names.map((item) => {
    return {
      value: item.npi,
      description: item.name,
    } as DropdownOption;
  });
};

export const parseNamesFromDropdownValue = (optionValue: string) => {
  if (!optionValue) {
    return null;
  }
  const [first_name, last_name] = optionValue.split('+');
  return { first_name, last_name };
};

export const constructTableFilterCookieKey = (
  tableName: TableName,
  columnName: CookieColumnName | 'drug_category',
  // we're adding the userEmail to the cookie key to avoid conflicts if a device is shared
  // between multiple users
  userEmail?: string,
) => {
  return `${userEmail}_${tableName}_table_${columnName}`;
};

export const getTableFilterValuesFromCookie = (
  tableName: TableName,
  columnName: CookieColumnName | 'drug_category',
  // we're adding the userEmail to the cookie key to avoid conflicts if a device is shared
  // between multiple users
  userEmail?: string,
) => {
  const cookieKey = constructTableFilterCookieKey(tableName, columnName, userEmail);

  try {
    return JSON.parse(getCookie(cookieKey) || '[]');
  } catch (error) {
    console.error(error);
    Sentry.captureException(error, {
      extra: {
        cookieKey,
        cookieValue: getCookie(cookieKey),
      },
    });
    return [];
  }
};

// this method is now only used for the RxTable office filters because that's the only filter
// that uses both search params and cookies
export const getDropdownInitialValueFromSearchParamsAndCookie = (
  fullUrlParamsString: string = '',
  tableName: TableName,
  columnName: CookieColumnName,
  // the optionsList is used to get the user-friendly description for the option from
  // the value, if needed
  optionsList?: DropdownOption[],
): DropdownOption[] => {
  // url params take precedence over cookie values, so we only get the cookie values if there are no url params to use
  if (!fullUrlParamsString) {
    return tableName && columnName ? getTableFilterValuesFromCookie(tableName, columnName) : [];
  }

  return fullUrlParamsString.split(MULTI_SELECT_DELINEATOR).map((substring) => {
    if (optionsList) {
      const descriptionFromValue = substring.replace('+', ' ');
      const descriptionFromOptionsList = optionsList.find((option) => option.value === substring)?.description;
      return {
        value: substring,
        description: descriptionFromOptionsList || descriptionFromValue,
      };
    }
    return {
      value: substring,
      description: substring.replace('+', ' '),
    };
  });
};

export const getDropdownInitialValueFromSearchParams = (
  userDefaultFilters: UserDefaultFilters | null | undefined,
  fullUrlParamsString: string = '',
  columnName?: 'offices' | 'providers' | 'drugs' | 'drug_category',
  // the optionsList is used to get the user-friendly description for the option from
  // the value, if needed
  optionsList?: DropdownOption[],
): DropdownOption[] => {
  // url params take precedence over default filters values, so we only get the default filters values if there are no url params to use
  if (!fullUrlParamsString) {
    if (columnName && userDefaultFilters) {
      const defaultFilterItems = userDefaultFilters[columnName as keyof UserDefaultFilters] || [];
      return defaultFilterItems.map((item) => ({
        value: ('id' in item ? item.id : item.npi) as string,
        description: item.name,
      }));
    } else {
      return [];
    }
  }

  return fullUrlParamsString.split(MULTI_SELECT_DELINEATOR).map((substring) => {
    if (optionsList) {
      const descriptionFromValue = substring.replace('+', ' ');
      const descriptionFromOptionsList = optionsList.find((option) => option.value === substring)?.description;
      return {
        value: substring,
        description: descriptionFromOptionsList || descriptionFromValue,
      };
    }
    return {
      value: substring,
      description: substring.replace('+', ' '),
    };
  });
};

// This is passed to the MultiselectAutocomplete component. Do not pass this to the
// table column filters; the results are in a different format than the column filters need.
export const getInitialMultiselectFilterValueFromCookie = (
  tableName: TableName,
  columnName: CookieColumnName,
  userEmail?: string,
) => {
  return getTableFilterValuesFromCookie(tableName, columnName, userEmail);
};

// This is passed to the table column filter for frontend table filtering. Do not pass this to the
// MultiselectAutocomplete component; the results are in a different format than the MultiselectAutocomplete needs.
export const getInitialTableColumnFilterValueFromCookie = (
  tableName: TableName,
  columnName: CookieColumnName,
  userEmail?: string,
) => {
  return getTableFilterValuesFromCookie(tableName, columnName, userEmail).map(
    (option: MultiselectAutocompleteOption) => option.value,
  );
};

export const updateTableColumnFilterValueCookie = (
  value: MultiselectAutocompleteOption[],
  tableName: TableName,
  columnName: CookieColumnName,
  userEmail?: string,
) => {
  const cookieKey = constructTableFilterCookieKey(tableName, columnName, userEmail);

  if (!value.length) {
    deleteCookie(cookieKey);
  } else {
    createCookie(cookieKey, JSON.stringify(value));
  }
};

export const dedupeAndSortOptions = (sortFn: (a: string, b: string) => number, options: DropdownOption[]) => {
  const dedupedOptions = options.filter(
    (option, index, self) => index === self.findIndex((o) => o.description === option.description),
  );
  const sortedOptions: DropdownOption[] = [];
  const sortedDescriptions = dedupedOptions.map((option) => option.description).sort(sortFn);
  const descriptionsToOptionsMap = new Map(dedupedOptions.map((option) => [option.description, option]));
  sortedDescriptions.forEach((description) => {
    const option = descriptionsToOptionsMap.get(description);
    if (option) {
      sortedOptions.push(option);
    }
  });
  return sortedOptions;
};

export const getAddressString = (address: PharmacyAddress) => {
  return [address.name, address.line1, address.line2, `${address.city}, ${address.state} ${address.postal_code}`]
    .filter(Boolean)
    .join(', ');
};

export const formatName = (name: string) => {
  const parts = name.toLowerCase().split(' ');
  if (parts.length <= 2) {
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  const last = parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
  const middle = parts
    .slice(1, -1)
    .map((part) => part.charAt(0).toUpperCase().concat('.'))
    .join(' ');
  return `${first} ${middle} ${last}`;
};

/**
 * Replaces text on the page with new text.
 * @param searchText - The text to search for.
 * @param replaceText - The text to replace the search text with.
 * @param querySelector - The CSS selector to search for. Defaults to all elements.
 */
export const replaceTextOnPage = (searchText: string, replaceText: string, querySelector: string = '*') => {
  const elements = document.querySelectorAll(querySelector);

  elements.forEach((element) => {
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.includes(searchText)) {
        node.nodeValue = node.nodeValue.replaceAll(searchText, replaceText);
      }
    });
  });
};

export const areStringArraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

export function applySavedFilters(
  authContext: {
    user: User | null;
    setUser: (user: User | null) => void;
  },
  providersWithNames: { npi: string; name: string }[],
  queryClient?: QueryClient,
) {
  if (!authContext.user) {
    return;
  }

  // Update the user context with the new filter data so the user sees
  // the effects of the filter change immediately
  authContext.setUser({
    ...authContext.user,
    default_filters: { providers: providersWithNames },
  });

  // Refetch notifications query to update red dots in sidebar
  if (queryClient) {
    queryClient.refetchQueries({ queryKey: ['notifications'] });
  }
}
